import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import MainLayout from '../../components/layout/MainLayout';
import {
  getTwilioBillingItems,
  getTwilioBillingSummary,
  getTwilioBillingTenants,
} from '../../api/backendApi';
import GoogleBillingSection from './GoogleBillingSection';
import { ALL_TENANTS } from './supportNav';
import {
  PRICING_HELP,
  currentUtcMonth,
  monthlyPricingState,
  formatDirection,
  formatDuration,
  formatFeature,
  formatLeg,
  formatUtc,
  formatVendorAmount,
  formatVendorCosts,
} from './billingFormat';

function unwrap(response) {
  if (response && response.success === true && response.data) return response.data;
  return response || {};
}

function StatusChip({ label, color = 'default' }) {
  if (!label) return null;
  const tip = PRICING_HELP[label];
  const chip = <Chip size="small" label={label} color={color} variant="outlined" />;
  return tip ? <Tooltip title={tip}><span>{chip}</span></Tooltip> : chip;
}

function pricingColor(label) {
  if (label === 'Priced') return 'success';
  if (label === 'Pricing failed') return 'error';
  if (label === 'Retry scheduled') return 'info';
  if (label === 'Waiting for Twilio') return 'warning';
  if (label === 'Unallocated') return 'warning';
  return 'default';
}

function CostCards({ currencies, pricesPending = false }) {
  if (!currencies?.length) {
    return (
      <article className="rounded border border-gray-200 bg-white p-4" data-testid="cost-empty">
        <p className="text-sm text-gray-500">Total priced cost</p>
        <p className="mt-1 text-2xl font-semibold text-gray-900">
          {pricesPending ? 'Not available yet' : 'No priced cost yet'}
        </p>
      </article>
    );
  }
  return currencies.map((row) => (
    <section key={row.currency} className="contents" data-testid={`currency-${row.currency}`}>
      {[
        ['Total priced cost', row.pricedVendorCost],
        ['Voice cost', row.voiceCost],
        ['SMS cost', row.smsCost],
      ].map(([label, amount]) => (
        <article key={`${row.currency}-${label}`} className="rounded border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">
            {label}
            <span className="ml-2 font-medium text-gray-700">{row.currency}</span>
          </p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">
            {formatVendorAmount(amount, row.currency)}
          </p>
        </article>
      ))}
    </section>
  ));
}

const selectSx = { minWidth: 140, background: '#fff' };

function monthFromQuery(value) {
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(value || '') ? value : currentUtcMonth();
}

export default function BillingPage({
  embedded = false,
  lockedTenantId = '',
  month: controlledMonth,
  onMonthChange,
} = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTenantId = String(lockedTenantId || searchParams.get('tenantId') || '').trim();
  const linkedTenantId = rawTenantId && rawTenantId !== ALL_TENANTS ? rawTenantId : '';
  const vendor = searchParams.get('vendor') === 'google' ? 'google' : 'twilio';
  const [month, setMonth] = useState(() => monthFromQuery(controlledMonth || searchParams.get('month')));
  const [linkedMissing, setLinkedMissing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [cursorStack, setCursorStack] = useState([]);
  const [activeCursor, setActiveCursor] = useState(null);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState('');
  const [filters, setFilters] = useState({
    channel: '',
    feature: '',
    pricingStatus: '',
    attributionStatus: '',
  });

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [summaryResp, tenantResp] = await Promise.all([
        getTwilioBillingSummary(month),
        getTwilioBillingTenants(month),
      ]);
      setSummary(unwrap(summaryResp));
      setTenants(unwrap(tenantResp).tenants || []);
    } catch (err) {
      setSummary(null);
      setTenants([]);
      setError(err?.message || 'Billing data could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [summaryResp, tenantResp] = await Promise.all([
          getTwilioBillingSummary(month),
          getTwilioBillingTenants(month),
        ]);
        if (cancelled) return;
        setSummary(unwrap(summaryResp));
        setTenants(unwrap(tenantResp).tenants || []);
      } catch (err) {
        if (cancelled) return;
        setSummary(null);
        setTenants([]);
        setError(err?.message || 'Billing data could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [month, refreshTick]);

  useEffect(() => {
    if (!selected) return undefined;
    let cancelled = false;
    (async () => {
      setItemsLoading(true);
      setItemsError('');
      try {
        const response = await getTwilioBillingItems({
          month,
          tenantId: selected.tenantId,
          cursor: activeCursor || undefined,
          channel: filters.channel || undefined,
          feature: filters.feature || undefined,
          pricingStatus: filters.pricingStatus || undefined,
          attributionStatus: filters.attributionStatus || undefined,
          pageSize: 50,
        });
        if (cancelled) return;
        const data = unwrap(response);
        setItems(data.items || []);
        setNextCursor(data.nextCursor || null);
      } catch (err) {
        if (cancelled) return;
        setItems([]);
        setNextCursor(null);
        setItemsError(err?.message || 'Usage records could not be loaded.');
      } finally {
        if (!cancelled) setItemsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, month, filters, activeCursor, refreshTick]);

  const currencies = summary?.currencies || [];
  const pending = summary?.pendingPriceCount || 0;
  const freshness = summary?.dataFreshnessAt || summary?.generatedAt;
  const pricingState = monthlyPricingState(summary);
  const pricesPending = (summary?.totalItems || 0) > 0 && currencies.length === 0 && pending > 0;

  const tenantCost = useMemo(() => {
    if (!selected) return '—';
    return formatVendorCosts(selected.vendorCostByCurrency);
  }, [selected]);

  useEffect(() => {
    if (controlledMonth) setMonth(monthFromQuery(controlledMonth));
  }, [controlledMonth]);

  useEffect(() => {
    if (!linkedTenantId || loading) return undefined;
    const row = tenants.find((item) => item.tenantId === linkedTenantId);
    if (row) {
      setLinkedMissing(false);
      openTenant(row);
    } else {
      setLinkedMissing(true);
    }
    return undefined;
  }, [linkedTenantId, tenants, loading]);

  function selectVendor(nextVendor) {
    const next = new URLSearchParams(searchParams);
    if (nextVendor === 'google') next.set('vendor', 'google');
    else next.delete('vendor');
    setSearchParams(next, { replace: true });
  }

  function openTenant(row) {
    setSelected(row);
    setActiveCursor(null);
    setCursorStack([]);
    setItems([]);
  }

  function changeFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setActiveCursor(null);
    setCursorStack([]);
  }

  const page = (
      <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 }, maxWidth: 1200, mx: embedded ? 'none' : 'auto' }} data-testid="billing-page">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 650, color: '#111827' }}>
              Billing
            </Typography>
            <Typography variant="body1" sx={{ color: '#4b5563', mt: 0.5 }}>
              Twilio vendor usage and costs by tenant
            </Typography>
            <p className="mt-2 text-sm font-medium text-amber-800">Vendor cost — not customer invoices</p>
            <p className="text-sm text-gray-600">
              Amounts stay in the currency Twilio reported. Currencies are never added together.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <TextField
              label="Billing month"
              type="month"
              size="small"
              value={month}
              onChange={(event) => {
                const value = event.target.value;
                setMonth(value);
                onMonthChange?.(value);
                if (!embedded) {
                  const next = new URLSearchParams(searchParams);
                  next.set('month', value);
                  if (rawTenantId) next.set('tenantId', rawTenantId);
                  setSearchParams(next, { replace: true });
                }
                setActiveCursor(null);
                setCursorStack([]);
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'aria-label': 'Billing month' }}
              sx={{ background: '#fff', minWidth: 180 }}
            />
            <Button variant="outlined" onClick={() => setRefreshTick((n) => n + 1)} disabled={loading}>
              Refresh
            </Button>
          </div>
        </div>

        <div className="mt-4 flex gap-2" data-testid="billing-vendor-tabs">
          <Button variant={vendor === 'twilio' ? 'contained' : 'outlined'} onClick={() => selectVendor('twilio')}>
            Twilio
          </Button>
          <Button variant={vendor === 'google' ? 'contained' : 'outlined'} onClick={() => selectVendor('google')} data-testid="vendor-google">
            Google Cloud
          </Button>
        </div>
        <p className="mt-3 text-sm text-gray-600">Google and Twilio costs are shown separately</p>

        {vendor === 'google' ? (
          <GoogleBillingSection month={month} linkedTenantId={linkedTenantId} refreshTick={refreshTick} />
        ) : (
        <>

        <p className="mt-3 text-sm text-gray-600" data-testid="freshness">
          Data freshness: {loading ? 'Loading…' : formatUtc(freshness)}
          {summary?.monthStart ? ` · UTC ${summary.monthStart} to ${summary.monthEndExclusive} (end exclusive)` : ''}
        </p>

        {error ? (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            action={
              <Button color="inherit" size="small" onClick={loadReport}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        ) : null}

        {!loading && linkedMissing ? (
          <Alert severity="info" sx={{ mt: 2 }} data-testid="linked-tenant-missing">
            No Twilio usage for {linkedTenantId} in {month}. Costs are not estimated here.
          </Alert>
        ) : null}

        {loading ? (
          <p className="mt-6 text-gray-600" data-testid="billing-loading">
            Loading billing data…
          </p>
        ) : null}

        {!loading && !error && summary?.truncated ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This month has more records than the read limit ({summary.monthReadLimit}). Totals are incomplete.
          </Alert>
        ) : null}

        {!loading && !error && pending > 0 ? (
          <Alert severity="warning" sx={{ mt: 2 }} data-testid="pending-banner">
            Prices are still arriving from Twilio ({summary.pendingNotRequestedCount || 0} waiting,{' '}
            {summary.retryScheduledCount || 0} retry scheduled). The priced total is not final.
          </Alert>
        ) : null}

        {!loading && !error && pricingState.id === 'none' && !linkedMissing ? (
          <Alert severity="info" sx={{ mt: 2 }} data-testid="empty-month">
            No Twilio usage in {month}.
          </Alert>
        ) : null}

        {!loading && !error && pricingState.id !== 'none' ? (
          <Alert
            severity={pricingState.id === 'attention' ? 'error' : pricingState.id === 'complete' ? 'success' : 'warning'}
            sx={{ mt: 2 }}
            data-testid="pricing-state"
          >
            {pricingState.label}
          </Alert>
        ) : null}

        {!loading && !error ? (
          <>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="usage-counts">
              {[
                ['Logical calls', summary?.logicalCallCount ?? 0],
                ['Twilio Voice resources', summary?.voiceResourceCount ?? 0],
                ['Voice duration', formatDuration(summary?.voiceDurationSeconds)],
                ['SMS count', summary?.smsResourceCount ?? 0],
                ['Priced resources', (currencies || []).reduce((sum, row) => sum + Number(row.pricedItems || 0), 0)],
                ['Pending resources', pending],
                ['Failed resources', summary?.failedPricingCount || 0],
                ['Vendor cost by currency', pricesPending ? 'Not available yet' : formatVendorCosts(currencies.map((row) => ({ currency: row.currency, amount: row.pricedVendorCost })))],
              ].map(([label, value]) => (
                <article key={label} className="rounded border border-gray-200 bg-white p-3">
                  <p className="text-xs text-gray-500">{label}</p>
                  <p className="mt-1 text-lg font-semibold text-gray-900">{value}</p>
                </article>
              ))}
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <CostCards currencies={currencies} pricesPending={pricesPending} />
              <article className="rounded border border-gray-200 bg-white p-4" data-testid="card-pending">
                <p className="text-sm text-gray-500">Pending prices</p>
                <p className="mt-1 text-2xl font-semibold text-gray-900">{pending}</p>
                <p className="text-xs text-gray-500">Waiting for Twilio or retry scheduled. Not included in priced cost.</p>
              </article>
              <article className="rounded border border-gray-200 bg-white p-4" data-testid="card-failed">
                <p className="text-sm text-gray-500">Failed prices</p>
                <p className="mt-1 text-2xl font-semibold text-gray-900">{summary?.failedPricingCount || 0}</p>
              </article>
              <article className="rounded border border-gray-200 bg-white p-4" data-testid="card-unallocated">
                <p className="text-sm text-gray-500">Unallocated items</p>
                <p className="mt-1 text-2xl font-semibold text-gray-900">{summary?.unallocatedCount || 0}</p>
              </article>
            </div>

            <div className="mt-6 overflow-x-auto rounded border border-gray-200 bg-white">
              <table className="min-w-[880px] w-full text-left text-sm" data-testid="tenant-table">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    {['Tenant', 'Calls/voice resources', 'Minutes', 'SMS', 'Priced', 'Pending', 'Failed', 'Twilio vendor cost', 'Last activity'].map((heading) => (
                      <th key={heading} className="px-3 py-2 font-medium whitespace-nowrap">
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tenants.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-3 py-6 text-gray-500">
                        No tenants have Twilio usage in this month.
                      </td>
                    </tr>
                  ) : (
                    tenants.map((row) => (
                      <tr
                        key={row.tenantId}
                        className="cursor-pointer border-t border-gray-100 hover:bg-blue-50"
                        data-testid={`tenant-row-${row.tenantId}`}
                        onClick={() => openTenant(row)}
                      >
                        <td className="px-3 py-2 font-medium text-gray-900">
                          <button type="button" className="text-left text-blue-700 underline-offset-2 hover:underline">
                            {row.tenantName}
                          </button>
                          {row.tenantId === 'unallocated' ? (
                            <span className="ml-2"><StatusChip label="Unallocated" color="warning" /></span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2">{row.voiceResourceCount ?? 0}</td>
                        <td className="px-3 py-2">{formatDuration(row.voiceDurationSeconds)}</td>
                        <td className="px-3 py-2">{row.smsCount ?? 0}</td>
                        <td className="px-3 py-2">{row.pricedCount ?? 0}</td>
                        <td className="px-3 py-2">{row.pendingCount ?? 0}</td>
                        <td className="px-3 py-2">{row.failedCount ?? 0}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{formatVendorCosts(row.vendorCostByCurrency)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{formatUtc(row.lastActivityAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : null}

        {lockedTenantId && selected ? (
          <section className="mt-4 rounded border border-gray-200 bg-white" data-testid="tenant-billing-inline">
            <BillingTenantPanel
              selected={selected}
              month={month}
              tenantCost={tenantCost}
              filters={filters}
              changeFilter={changeFilter}
              items={items}
              itemsError={itemsError}
              itemsLoading={itemsLoading}
              setRefreshTick={setRefreshTick}
              cursorStack={cursorStack}
              setCursorStack={setCursorStack}
              activeCursor={activeCursor}
              setActiveCursor={setActiveCursor}
              nextCursor={nextCursor}
              showClose={false}
            />
          </section>
        ) : null}

        <Drawer
          anchor="right"
          open={Boolean(selected) && !lockedTenantId}
          onClose={() => setSelected(null)}
          PaperProps={{ sx: { width: { xs: '100%', sm: 720 }, maxWidth: '100%' } }}
        >
          {selected ? (
            <BillingTenantPanel
              selected={selected}
              month={month}
              tenantCost={tenantCost}
              filters={filters}
              changeFilter={changeFilter}
              items={items}
              itemsError={itemsError}
              itemsLoading={itemsLoading}
              setRefreshTick={setRefreshTick}
              cursorStack={cursorStack}
              setCursorStack={setCursorStack}
              activeCursor={activeCursor}
              setActiveCursor={setActiveCursor}
              nextCursor={nextCursor}
              showClose
              onClose={() => setSelected(null)}
            />
          ) : null}
        </Drawer>
        </>
        )}
      </Box>
  );
  if (embedded) return page;
  return <MainLayout scrollMain>{page}</MainLayout>;
}

function BillingTenantPanel({
  selected,
  month,
  tenantCost,
  filters,
  changeFilter,
  items,
  itemsError,
  itemsLoading,
  setRefreshTick,
  cursorStack,
  setCursorStack,
  activeCursor,
  setActiveCursor,
  nextCursor,
  showClose = true,
  onClose,
}) {
  return (
    <Box sx={{ p: 2 }} data-testid="tenant-detail">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Typography variant="h6">{selected.tenantName}</Typography>
          <p className="text-sm text-gray-600">{month} · Vendor cost — not a customer charge</p>
        </div>
        {showClose ? <Button onClick={onClose}>Close</Button> : null}
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-2 text-sm md:grid-cols-3">
        <div><dt className="text-gray-500">Voice resources</dt><dd>{selected.voiceResourceCount ?? 0}</dd></div>
        <div><dt className="text-gray-500">Total minutes</dt><dd>{formatDuration(selected.voiceDurationSeconds)}</dd></div>
        <div><dt className="text-gray-500">SMS count</dt><dd>{selected.smsCount ?? 0}</dd></div>
        <div><dt className="text-gray-500">Vendor cost</dt><dd>{tenantCost}</dd></div>
        <div><dt className="text-gray-500">Pending</dt><dd>{selected.pendingCount ?? 0}</dd></div>
        <div><dt className="text-gray-500">Failed</dt><dd>{selected.failedCount ?? 0}</dd></div>
      </dl>
      <div className="mt-4 flex flex-wrap gap-2">
        <TextField select size="small" label="Voice/SMS" value={filters.channel} onChange={(e) => changeFilter('channel', e.target.value)} sx={selectSx}>
          <MenuItem value="">Voice and SMS</MenuItem>
          <MenuItem value="voice">Voice</MenuItem>
          <MenuItem value="sms">SMS</MenuItem>
        </TextField>
        <TextField select size="small" label="Feature" value={filters.feature} onChange={(e) => changeFilter('feature', e.target.value)} sx={selectSx}>
          <MenuItem value="">All features</MenuItem>
          <MenuItem value="sip_paging">SIP paging</MenuItem>
          <MenuItem value="browser_pstn">Browser PSTN</MenuItem>
          <MenuItem value="sip_outbound">SIP outbound</MenuItem>
          <MenuItem value="sms_outbound">SMS</MenuItem>
        </TextField>
        <TextField select size="small" label="Pricing status" value={filters.pricingStatus} onChange={(e) => changeFilter('pricingStatus', e.target.value)} sx={selectSx}>
          <MenuItem value="">All pricing</MenuItem>
          <MenuItem value="priced">Priced</MenuItem>
          <MenuItem value="not_requested">Waiting for Twilio</MenuItem>
          <MenuItem value="retry_scheduled">Retry scheduled</MenuItem>
          <MenuItem value="failed">Pricing failed</MenuItem>
        </TextField>
        <TextField select size="small" label="Attribution" value={filters.attributionStatus} onChange={(e) => changeFilter('attributionStatus', e.target.value)} sx={selectSx}>
          <MenuItem value="">All attribution</MenuItem>
          <MenuItem value="attributed">Attributed</MenuItem>
          <MenuItem value="unallocated">Unallocated</MenuItem>
        </TextField>
      </div>
      {itemsError ? (
        <Alert severity="error" sx={{ mt: 2 }} action={<Button color="inherit" size="small" onClick={() => setRefreshTick((n) => n + 1)}>Retry</Button>}>
          {itemsError}
        </Alert>
      ) : null}
      {itemsLoading ? <p className="mt-3 text-sm text-gray-600">Loading usage…</p> : null}
      <div className="mt-3 overflow-x-auto">
        <table className="min-w-[760px] w-full text-left text-sm" data-testid="usage-table">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              {['Date/time', 'Type', 'Feature', 'Direction', 'Status', 'Duration', 'Parent/child', 'Pricing status', 'Vendor cost', 'Masked SID'].map((heading) => (
                <th key={heading} className="px-2 py-2 font-medium whitespace-nowrap">{heading}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !itemsLoading ? (
              <tr>
                <td colSpan={10} className="px-2 py-4 text-gray-500">No usage records for this filter.</td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="border-t border-gray-100" data-testid={`usage-${item.id}`}>
                  <td className="px-2 py-2 whitespace-nowrap">{formatUtc(item.occurredAt)}</td>
                  <td className="px-2 py-2">{item.channel === 'sms' ? 'SMS' : item.channel === 'voice' ? 'Voice' : item.resourceType || '—'}</td>
                  <td className="px-2 py-2 whitespace-nowrap">{formatFeature(item.feature)}</td>
                  <td className="px-2 py-2">{formatDirection(item.direction)}</td>
                  <td className="px-2 py-2">{item.twilioStatus || item.status || '—'}</td>
                  <td className="px-2 py-2">{formatDuration(item.durationSeconds)}</td>
                  <td className="px-2 py-2">{formatLeg(item.leg)}</td>
                  <td className="px-2 py-2">
                    <span className="inline-flex flex-wrap gap-1">
                      <StatusChip label={item.pricingLabel} color={pricingColor(item.pricingLabel)} />
                      {item.unallocated ? <StatusChip label="Unallocated" color="warning" /> : null}
                      {item.reconciliationLabel === 'Reconciled' ? <StatusChip label="Reconciled" color="success" /> : null}
                    </span>
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    {item.normalizedVendorCost ? formatVendorAmount(item.normalizedVendorCost, item.currency) : '—'}
                  </td>
                  <td className="px-2 py-2 font-mono text-xs">{item.maskedSid}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex gap-2">
        <Button
          variant="outlined"
          disabled={cursorStack.length === 0 || itemsLoading}
          onClick={() => {
            const previous = cursorStack[cursorStack.length - 1] ?? null;
            setCursorStack((stack) => stack.slice(0, -1));
            setActiveCursor(previous);
          }}
        >
          Previous
        </Button>
        <Button
          variant="outlined"
          disabled={!nextCursor || itemsLoading}
          onClick={() => {
            setCursorStack((stack) => [...stack, activeCursor]);
            setActiveCursor(nextCursor);
          }}
          data-testid="usage-next"
        >
          Next
        </Button>
      </div>
    </Box>
  );
}
